import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useResourceCatalog } from '../../hooks/useResourceCatalog'
import type { Control } from 'react-hook-form'
import type { DataAccessFormValues } from '../../types'

interface ResourcePickerProps {
    control: Control<DataAccessFormValues>
}

/**
 * Grouped resource dropdown — pulls catalog from useResourceCatalog.
 * Replaces freeform keycloak_name text input; prevents typos.
 */
const ResourcePicker = ({ control }: ResourcePickerProps) => {
    const { t, i18n } = useTranslation('settings')
    const { groups, isLoading } = useResourceCatalog()

    return (
        <Controller
            control={control}
            name="keycloak_name"
            render={({ field, fieldState }) => (
                <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                        {t('data-access-resource')}
                        <span className="text-red-500 ml-1">*</span>
                    </label>
                    <select
                        {...field}
                        disabled={isLoading}
                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-50 disabled:text-gray-400"
                    >
                        <option value="">
                            {isLoading
                                ? t('loading')
                                : t('data-access-resource-placeholder')}
                        </option>
                        {groups.map((group) => (
                            <optgroup
                                key={group.key}
                                label={
                                    i18n.language === 'vi'
                                        ? group.label_vi
                                        : group.label_en
                                }
                            >
                                {group.resources.map((r) => (
                                    <option
                                        key={r.key}
                                        value={r.key}
                                        title={
                                            i18n.language === 'vi'
                                                ? r.description_vi
                                                : r.description_en
                                        }
                                    >
                                        {i18n.language === 'vi'
                                            ? r.label_vi
                                            : r.label_en}
                                    </option>
                                ))}
                            </optgroup>
                        ))}
                    </select>
                    {fieldState.error && (
                        <p className="text-xs text-red-500">
                            {fieldState.error.message}
                        </p>
                    )}
                </div>
            )}
        />
    )
}

export default ResourcePicker
